import React from "react";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight,
} from "lucide-react";

export const PAGE_SIZE_OPTIONS = [20, 50, 100, 250, 500];

/**
 * Checkboxes de "Buscar por" reusables.
 * Cada filtro es un campo controlado: SKU / Nombre / Código de barras / Descripción.
 */
export function SearchFilters({ filters, onChange, testidPrefix = "search" }) {
  const toggle = (key) => onChange({ ...filters, [key]: !filters[key] });
  const Item = ({ k, label }) => (
    <label className="flex items-center gap-1.5 text-xs cursor-pointer select-none">
      <input
        type="checkbox"
        className="accent-primary cursor-pointer"
        checked={!!filters[k]}
        onChange={() => toggle(k)}
        data-testid={`${testidPrefix}-filter-${k}`}
      />
      <span className={filters[k] ? "font-semibold" : "text-muted-foreground"}>{label}</span>
    </label>
  );
  return (
    <div className="flex items-center gap-3 flex-wrap" data-testid={`${testidPrefix}-filters`}>
      <span className="overline text-xs">Buscar por:</span>
      <Item k="in_sku" label="SKU" />
      <Item k="in_name" label="Nombre" />
      <Item k="in_barcode" label="Código de barras" />
      <Item k="in_description" label="Descripción" />
    </div>
  );
}

/**
 * Footer de paginación: selector de tamaño + navegación primera/anterior/siguiente/última.
 */
export function PaginationBar({
  page, pageSize, total, totalPages, onPageChange, onPageSizeChange,
  testidPrefix = "pag",
}) {
  const from = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const to = Math.min(page * pageSize, total);
  return (
    <div className="flex items-center justify-between gap-4 p-3 border-t border-border flex-wrap" data-testid={`${testidPrefix}-bar`}>
      <div className="flex items-center gap-2 text-xs text-muted-foreground">
        <span>Mostrar</span>
        <Select value={String(pageSize)} onValueChange={(v) => onPageSizeChange(Number(v))}>
          <SelectTrigger className="h-7 w-20" data-testid={`${testidPrefix}-size`}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {PAGE_SIZE_OPTIONS.map((n) => (
              <SelectItem key={n} value={String(n)}>{n}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <span>· <b className="text-foreground">{from}-{to}</b> de <b className="text-foreground">{total}</b></span>
      </div>
      <div className="flex items-center gap-1">
        <Button size="icon" variant="ghost" disabled={page <= 1} onClick={() => onPageChange(1)} data-testid={`${testidPrefix}-first`}>
          <ChevronsLeft className="w-4 h-4" />
        </Button>
        <Button size="icon" variant="ghost" disabled={page <= 1} onClick={() => onPageChange(page - 1)} data-testid={`${testidPrefix}-prev`}>
          <ChevronLeft className="w-4 h-4" />
        </Button>
        <span className="text-xs px-2 font-bold">
          Página {page} de {totalPages || 1}
        </span>
        <Button size="icon" variant="ghost" disabled={page >= totalPages} onClick={() => onPageChange(page + 1)} data-testid={`${testidPrefix}-next`}>
          <ChevronRight className="w-4 h-4" />
        </Button>
        <Button size="icon" variant="ghost" disabled={page >= totalPages} onClick={() => onPageChange(totalPages)} data-testid={`${testidPrefix}-last`}>
          <ChevronsRight className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );
}

/**
 * Estado inicial recomendado para los filtros.
 */
export const DEFAULT_SEARCH_FILTERS = {
  in_sku: true,
  in_name: true,
  in_barcode: true,
  in_description: false,
};

/**
 * Convierte un objeto de filtros + query a query string lista para enviar.
 */
export function buildProductsQuery({ q = "", filters = DEFAULT_SEARCH_FILTERS, page = 1, pageSize = 50, onlyActive = false, extra = {} }) {
  const params = new URLSearchParams();
  if (q) params.set("q", q);
  for (const [k, v] of Object.entries(filters)) params.set(k, String(v));
  params.set("page", String(page));
  params.set("page_size", String(pageSize));
  if (onlyActive) params.set("only_active", "true");
  for (const [k, v] of Object.entries(extra)) {
    if (v !== null && v !== undefined && v !== "") params.set(k, String(v));
  }
  return params.toString();
}
